import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Question, AnswerType, Serie } from '../../types';
import { api, BASE_URL } from '../../utils/api';

interface QuestionFormProps {
  question?: Question;
  series?: Serie[];
  availableMusics?: string[];
  onSave: (q: Question) => void;
  onCancel: () => void;
}

const emptyForm = {
  title: '',
  songTitle: '',
  artist: '',
  answerType: 'text' as AnswerType,
  durationMs: 30000,
  untimed: false,
  maxPoints: 100,
  dynamicScoring: false,
  choices: ['', ''],
  correctAnswer: '',
  startMs: 0,
  endMs: 0,
  questionOrder: 0,
  serieId: '',
};

function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function QuestionForm({ question, series = [], availableMusics = [], onSave, onCancel }: QuestionFormProps) {
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [dragging, setDragging] = useState<'start' | 'end' | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (question) {
      setForm({
        title: question.title,
        songTitle: question.songTitle || '',
        artist: question.artist || '',
        answerType: question.answerType,
        durationMs: question.durationMs > 0 ? question.durationMs : 30000,
        untimed: question.durationMs === 0,
        maxPoints: question.maxPoints,
        dynamicScoring: question.dynamicScoring,
        choices: question.choices.length >= 2 ? [...question.choices] : ['', ''],
        correctAnswer: question.correctAnswer || '',
        startMs: question.startMs,
        endMs: question.endMs,
        questionOrder: question.questionOrder,
        serieId: question.serieId ? String(question.serieId) : '',
      });
      if (question.audioFile) {
        setAudioPreviewUrl(`${BASE_URL}/api/musics/stream/${question.audioFile}`);
      }
    }
  }, [question]);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAudioFile(file);
    if (audioPreviewUrl && audioPreviewUrl.startsWith('blob:')) URL.revokeObjectURL(audioPreviewUrl);
    const url = URL.createObjectURL(file);
    setAudioPreviewUrl(url);
    setForm((f) => ({ ...f, startMs: 0, endMs: 0 }));

    // Auto-extract metadata
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('audio', file);
      const meta = await api.postForm<{ songTitle: string; artist: string }>('/api/questions/extract-metadata', fd);
      setForm((f) => ({
        ...f,
        songTitle: meta.songTitle && !f.songTitle ? meta.songTitle : f.songTitle,
        artist: meta.artist && !f.artist ? meta.artist : f.artist,
      }));
    } catch {
      // silent fail — user fills manually
    } finally {
      setExtracting(false);
    }
  };

  const handleAudioLoaded = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setAudioDuration(audio.duration * 1000);
    if (form.startMs > 0) audio.currentTime = form.startMs / 1000;
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTime(audio.currentTime * 1000);
    if (form.endMs > 0 && audio.currentTime >= form.endMs / 1000) {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      if (form.startMs > 0 && audio.currentTime < form.startMs / 1000) {
        audio.currentTime = form.startMs / 1000;
      }
      audio.play();
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const resetSegment = () => {
    setForm((f) => ({ ...f, startMs: 0, endMs: 0, durationMs: 30000 }));
    if (audioRef.current) audioRef.current.currentTime = 0;
  };

  // Timeline click to seek
  const handleTimelineClick = (e: React.MouseEvent) => {
    if (dragging) return;
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect || audioDuration <= 0) return;
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const ms = Math.floor(ratio * audioDuration);
    if (audioRef.current) audioRef.current.currentTime = ms / 1000;
  };

  // Drag handles
  useEffect(() => {
    if (!dragging) return;

    const getMs = (clientX: number): number => {
      const rect = timelineRef.current?.getBoundingClientRect();
      if (!rect || audioDuration <= 0) return 0;
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return Math.floor(ratio * audioDuration);
    };

    const onMove = (e: MouseEvent) => {
      const ms = getMs(e.clientX);
      if (dragging === 'start') {
        setForm((f) => {
          const newStart = Math.min(ms, f.endMs > 0 ? f.endMs - 500 : audioDuration - 500);
          const clamped = Math.max(0, newStart);
          if (audioRef.current) audioRef.current.currentTime = clamped / 1000;
          return { ...f, startMs: clamped };
        });
      } else {
        setForm((f) => {
          const newEnd = Math.min(Math.max(ms, f.startMs + 500), audioDuration);
          if (audioRef.current) audioRef.current.currentTime = newEnd / 1000;
          const durationMs = newEnd > f.startMs ? newEnd - f.startMs : f.durationMs;
          return { ...f, endMs: newEnd, durationMs };
        });
      }
    };

    const onUp = () => setDragging(null);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [dragging, audioDuration]);

  const setChoice = (i: number, val: string) => {
    const c = [...form.choices];
    c[i] = val;
    set('choices', c);
  };

  const addChoice = () => {
    if (form.choices.length < 6) set('choices', [...form.choices, '']);
  };

  const removeChoice = (i: number) => {
    if (form.choices.length > 2) {
      set('choices', form.choices.filter((_, idx) => idx !== i));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('songTitle', form.songTitle || '');
      fd.append('artist', form.artist || '');
      fd.append('answerType', form.answerType);
      fd.append('durationMs', form.untimed ? '0' : String(form.durationMs));
      fd.append('maxPoints', String(form.maxPoints));
      fd.append('dynamicScoring', String(form.dynamicScoring));
      fd.append('startMs', String(form.startMs));
      fd.append('endMs', String(form.endMs));
      fd.append('questionOrder', String(form.questionOrder));
      fd.append('serieId', form.serieId || '');

      const choices = form.answerType === 'multiple_choice'
        ? form.choices.filter(Boolean)
        : [];
      fd.append('choices', JSON.stringify(choices));
      fd.append('correctAnswer', form.correctAnswer || '');

      if (audioFile) fd.append('audio', audioFile);

      let saved: Question;
      if (question) {
        saved = await api.putForm<Question>(`/api/questions/${question.id}`, fd);
      } else {
        saved = await api.postForm<Question>('/api/questions', fd);
      }
      onSave(saved);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const segmentDuration = form.endMs > form.startMs ? form.endMs - form.startMs : 0;
  const startPct = audioDuration > 0 ? (form.startMs / audioDuration) * 100 : 0;
  const endPct = audioDuration > 0 ? (form.endMs / audioDuration) * 100 : 0;
  const cursorPct = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  return (
    <form className="question-form" onSubmit={handleSubmit}>
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Question title */}
      <div className="form-group">
        <label>Titre de la question *</label>
        <input
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          required
          placeholder="Ex: Quel artiste chante cette chanson ?"
        />
      </div>

      {/* Audio file — placed before songTitle to allow auto-fill */}
      <div className="form-group">
        <label>Fichier audio</label>
        <input
          type="file"
          accept=".flac,.mp3,.ogg,.wav,.m4a,audio/*"
          onChange={handleFileChange}
        />
        {extracting && <p className="form-hint">Extraction des métadonnées...</p>}
        {audioFile && !extracting && <p className="form-hint">✓ {audioFile.name}</p>}
        {question?.audioFile && !audioFile && (
          <p className="form-hint">Fichier actuel : {question.audioFile}</p>
        )}
      </div>

      {/* Song title + artist (auto-filled from metadata) */}
      <div className="form-row">
        <div className="form-group" style={{ flex: 2 }}>
          <label>Titre de la chanson {extracting && <span className="extracting-hint">auto-détection...</span>}</label>
          <input
            value={form.songTitle}
            onChange={(e) => set('songTitle', e.target.value)}
            placeholder="Ex: Bohemian Rhapsody"
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Artiste {extracting && <span className="extracting-hint">auto-détection...</span>}</label>
          <input
            value={form.artist}
            onChange={(e) => set('artist', e.target.value)}
            placeholder="Ex: Queen"
          />
        </div>
      </div>

      {/* Audio segment picker */}
      {audioPreviewUrl && (
        <div className="audio-picker">
          <audio
            ref={audioRef}
            src={audioPreviewUrl}
            onLoadedMetadata={handleAudioLoaded}
            onTimeUpdate={handleTimeUpdate}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            preload="metadata"
          />

          <div className="audio-controls">
            <button type="button" className="btn btn-sm btn-secondary" onClick={togglePlay}>
              {isPlaying ? '⏸ Pause' : '▶ Lecture'}
            </button>
            <span className="audio-time">
              {formatTime(currentTime)} / {formatTime(audioDuration)}
            </span>
            <button type="button" className="btn btn-sm btn-secondary" onClick={resetSegment} title="Réinitialiser le segment">
              ↺ Reset
            </button>
          </div>

          {/* Interactive timeline with drag handles */}
          {audioDuration > 0 && (
            <div className="timeline-wrapper">
              <div
                ref={timelineRef}
                className="audio-timeline"
                onClick={handleTimelineClick}
              >
                {/* Selected segment */}
                <div
                  className="timeline-segment"
                  style={{
                    left: `${startPct}%`,
                    width: `${form.endMs > form.startMs ? endPct - startPct : 0}%`,
                    minWidth: form.endMs > form.startMs ? 4 : 0,
                  }}
                />
                {/* Playback cursor */}
                <div className="timeline-cursor" style={{ left: `${cursorPct}%` }} />
                {/* Start handle */}
                {form.startMs > 0 && (
                  <div
                    className="timeline-handle timeline-handle-start"
                    style={{ left: `${startPct}%` }}
                    onMouseDown={(e) => { e.stopPropagation(); setDragging('start'); }}
                    title={`Début : ${formatTime(form.startMs)}`}
                  />
                )}
                {/* End handle */}
                {form.endMs > 0 && (
                  <div
                    className="timeline-handle timeline-handle-end"
                    style={{ left: `${endPct}%` }}
                    onMouseDown={(e) => { e.stopPropagation(); setDragging('end'); }}
                    title={`Fin : ${formatTime(form.endMs)}`}
                  />
                )}
              </div>
              <div className="timeline-labels">
                <span>0:00</span>
                <span>{formatTime(audioDuration)}</span>
              </div>
            </div>
          )}

          <div className="segment-markers">
            <div className="marker-group">
              <button type="button" className="btn btn-sm btn-marker" onClick={() => {
                const audio = audioRef.current;
                if (!audio) return;
                set('startMs', Math.floor(audio.currentTime * 1000));
              }}>
                ⬅ Marquer début
              </button>
              <span className="marker-value">{formatTime(form.startMs)}</span>
            </div>
            <div className="marker-group">
              <button type="button" className="btn btn-sm btn-marker" onClick={() => {
                const audio = audioRef.current;
                if (!audio) return;
                const ms = Math.floor(audio.currentTime * 1000);
                setForm((f) => ({
                  ...f,
                  endMs: ms,
                  durationMs: f.startMs > 0 && ms > f.startMs ? ms - f.startMs : f.durationMs,
                }));
              }}>
                Marquer fin ➡
              </button>
              <span className="marker-value">{formatTime(form.endMs)}</span>
            </div>
          </div>

          {segmentDuration > 0 && (
            <div className="segment-info">
              Segment : {formatTime(form.startMs)} → {formatTime(form.endMs)}
              <strong> ({Math.round(segmentDuration / 1000)}s)</strong>
              {' — '}Durée question : {Math.round(form.durationMs / 1000)}s
            </div>
          )}
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label>Type de réponse</label>
          <select value={form.answerType} onChange={(e) => set('answerType', e.target.value as AnswerType)}>
            <option value="text">Texte libre</option>
            <option value="buzzer">Buzzer</option>
            <option value="multiple_choice">Choix multiple</option>
          </select>
        </div>
        <div className="form-group">
          <label>Durée (secondes)</label>
          <input
            type="number" min="5" max="600"
            value={Math.round(form.durationMs / 1000)}
            onChange={(e) => set('durationMs', Math.round((parseFloat(e.target.value) || 30) * 1000))}
            disabled={form.untimed}
          />
          <label className="checkbox-label" style={{ marginTop: 4 }}>
            <input
              type="checkbox"
              checked={form.untimed}
              onChange={(e) => set('untimed', e.target.checked)}
            />
            Sans minuterie (l'admin arrête manuellement)
          </label>
        </div>
        <div className="form-group">
          <label>Points max</label>
          <input
            type="number" min="1"
            value={form.maxPoints}
            onChange={(e) => set('maxPoints', parseInt(e.target.value) || 100)}
          />
        </div>
      </div>

      {series.length > 0 && (
        <div className="form-group">
          <label>Série</label>
          <select value={form.serieId} onChange={(e) => set('serieId', e.target.value)}>
            <option value="">Aucune</option>
            {series.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label className="checkbox-label">
          <input type="checkbox" checked={form.dynamicScoring} onChange={(e) => set('dynamicScoring', e.target.checked)} />
          Score dynamique (vitesse + concurrence)
        </label>
      </div>

      {/* Multiple choice options */}
      {form.answerType === 'multiple_choice' && (
        <div className="form-group">
          <label>Propositions (sélectionne la bonne)</label>
          {form.choices.map((c, i) => (
            <div key={i} className="choice-row">
              <input
                type="radio"
                name="correctChoice"
                checked={form.correctAnswer === c && c !== ''}
                onChange={() => set('correctAnswer', c)}
              />
              <input value={c} onChange={(e) => setChoice(i, e.target.value)} placeholder={`Option ${i + 1}`} />
              {form.choices.length > 2 && (
                <button type="button" className="btn-icon" onClick={() => removeChoice(i)}>✕</button>
              )}
            </div>
          ))}
          {form.choices.length < 6 && (
            <button type="button" className="btn btn-sm" onClick={addChoice}>+ Ajouter option</button>
          )}
        </div>
      )}

      {/* Correct answer for text/buzzer */}
      {(form.answerType === 'text' || form.answerType === 'buzzer') && (
        <div className="form-group">
          <label>Bonne réponse (référence)</label>
          <input
            value={form.correctAnswer}
            onChange={(e) => set('correctAnswer', e.target.value)}
            placeholder="Référence affichée lors de la révélation"
          />
        </div>
      )}

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Annuler</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Sauvegarde...' : question ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
}
